import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { GestureResponderEvent } from "@-/com.npmjs.react-native";
import { Pressable } from "@-/com.npmjs.react-native";
import { useRef } from "react";
import { LegalDocumentSheet, type LegalDocumentSheetHandle } from "#legal-document-sheet";
import { PrivacyDocumentBody } from "#privacy-document-body";
import { HANIVERSE_PRIVACY_URL, HANIVERSE_TERMS_URL } from "#legal-urls";
import { TermsDocumentBody } from "#terms-document-body";

/**
 * An inline consent sentence — `{leadIn}` followed by pressable "Terms and Conditions" and
 * "Privacy Policy" links. Tapping a link opens a bottom sheet with that document's bundled
 * (offline) text plus a link to the latest hosted version. Hosts both sheets internally, so
 * a consumer only supplies the lead-in wording (e.g. "By using Haniverse, you agree to Haniverse's ").
 */
export function LegalAgreementNotice({
  className,
  leadIn,
  onPress,
  onPrivacyPress,
  onTermsPress,
}: {
  className?: string;
  leadIn: string;
  /** Fires when the non-link portions of the sentence are pressed (nested link presses are excluded). */
  onPress?: () => void;
  onPrivacyPress?: () => void;
  onTermsPress?: () => void;
}) {
  const termsSheetRef = useRef<LegalDocumentSheetHandle>(null);
  const privacySheetRef = useRef<LegalDocumentSheetHandle>(null);

  // A nested `<Text onPress>` inside an outer `<Text onPress>` makes RN highlight only the outer
  // node's own text runs (the lead-in and connectors) while skipping the link children — a patchy
  // "highlight around the non-links" artifact. So the sentence tap lives on a wrapping `Pressable`
  // (a single press target, no text highlight), and each link stops propagation so opening a sheet
  // doesn't also fire the sentence `onPress`. `suppressHighlighting` keeps link taps from painting
  // the same per-run highlight.
  const openTerms = (event: GestureResponderEvent) => {
    event.stopPropagation();
    if (onTermsPress) {
      onTermsPress();
    } else {
      termsSheetRef.current?.open();
    }
  };
  const openPrivacy = (event: GestureResponderEvent) => {
    event.stopPropagation();
    if (onPrivacyPress) {
      onPrivacyPress();
    } else {
      privacySheetRef.current?.open();
    }
  };

  return (
    <>
      <Pressable className={className} onPress={onPress}>
        <Text className="text-xs text-muted-foreground">
          {leadIn}
          <Text
            className="text-xs text-foreground underline"
            onPress={openTerms}
            suppressHighlighting
          >
            Terms and Conditions
          </Text>
          {" and "}
          <Text
            className="text-xs text-foreground underline"
            onPress={openPrivacy}
            suppressHighlighting
          >
            Privacy Policy
          </Text>
          .
        </Text>
      </Pressable>

      <LegalDocumentSheet
        latestVersionUrl={HANIVERSE_TERMS_URL}
        noticeLeadIn="To see the latest version of these Terms, visit "
        ref={termsSheetRef}
      >
        <TermsDocumentBody />
      </LegalDocumentSheet>
      <LegalDocumentSheet
        latestVersionUrl={HANIVERSE_PRIVACY_URL}
        noticeLeadIn="To see the latest version of this Privacy Policy, visit "
        ref={privacySheetRef}
      >
        <PrivacyDocumentBody />
      </LegalDocumentSheet>
    </>
  );
}
